// ABOUTME: Point-tag — one floating label anchored to a spot on the reel video (SPEC-26).
// ABOUTME: Desktop is a single line (Figma redesign 2026-09-04); mobile keeps its two-line plate.

import React from 'react';
import { revealStyle, Reveal, REVEAL_MS } from '../../../animation/revealTransition';

// Authored values from the Figma frames, in each composition's own native px space (desktop frames
// are 3840 wide, mobile 1216). They are multiplied by the live video-box scale rather than used
// literally: at a 1920px viewport the desktop frame renders at 0.5x, so an unscaled 42px tag would
// be twice its designed size relative to the watch it labels. D1's "fluid within the composition".
//
// ── The 2026-09-04 redesign ─────────────────────────────────────────────────
// Every DESKTOP chapter's tags were re-authored as ONE line. `369:113` on movement — and the
// matching frames on dial (`369:579`/`369:581`/`369:583`), crown (`369:88`/`369:90`/`369:92`) and
// exploded — is a 135.667-tall plate holding a single 42px Gilroy-Medium line in white, on the
// same frosted panel with the same 53.333 padding and 37.333 radius as before.
//
// What is gone is the hierarchy: the 75px ExtraBold headline, the 26.667px dim label beneath it,
// the 112px side-by-side numeral, and the bordered secondary description block. With those goes
// the plate's height — the authored frame is 135.667 against the old 229-260 — which is the "much
// smaller" in the client's note. Font size alone went UP (37.333 -> 42); it is the second line
// that made the old tag big, so do not read this table as a shrink and re-tune 42 downward.
//
// Mobile was NOT re-authored. `45:3294` and `45:3062` still set two lines at 37.333 in a
// bottom-anchored row (both re-rendered and checked on 2026-09-04, hence the note above the
// `flat` branch below). Padding 45 / radius 37 are the mobile tokens and 53.333 / 37.333 the
// desktop pair; crossing them is exactly the defect the Phase 4 Revision caught, so they stay
// named per composition rather than interpolated.
//
// ── Why the VERTICAL padding is derived and the horizontal one is not ───────
// Reported 2026-09-04: the panel renders visibly taller than the frame draws it. It did — by 17%
// — and the cause is that Figma and CSS measure vertical padding from different edges.
//
// These frames carry `text-box-edge: cap_alphabetic` + `text-box-trim: trim-both`, so Figma's box
// is padding + the CAP height of the type and its authored 53.333 sits above the capitals. CSS has
// no shipping equivalent to lean on: it pads from the LINE box, which at leading 1.24 is 52.08px
// against a 29.4px cap. Taking 53.333 literally therefore adds `42 * (1.24 - 0.70) = 22.68` native
// px of air the frame does not have — 2*53.333 + 52.08 = 158.75 rendered against 135.667 authored.
//
// Horizontal padding is UNAFFECTED, because the trim is block-direction only. That is why the
// report was "too tall" rather than "too big": measured on its own screenshot against the Figma
// frame at the same scale, the panel's width was within 2% and only its height was out.
//
// 0.70 is Gilroy's cap ratio, read off the frames rather than assumed — Figma reports text heights
// of 29 at 42px and 39 at 55px — and it reproduces all three authored boxes: the tag at
// 2*53.333 + 42*0.70 = 136.07 (authored 135.667), the carousel plate at 199.56 (199.66), and the
// carousel header, which is NOT trimmed, at 2*53.333 + 55.149*1.242 = 175.16 (174.667).
export const CAP_HEIGHT_RATIO = 0.70;

/**
 * The CSS vertical padding that reproduces a cap-trimmed Figma box of the same authored padding.
 * Exported because CollectionOverlay's variant plates are trimmed the same way and had the same
 * defect (26% too tall, since two lines each contribute their own excess).
 */
export function capTrimmedPadding(authoredPadding, font, lineHeight) {
  return authoredPadding - (font * (lineHeight - CAP_HEIGHT_RATIO)) / 2;
}

const COMPOSITION = {
  desktop: {
    font: 42,
    padX: 53.333,
    padY: capTrimmedPadding(53.333, 42, 1.24), // 41.99
    radius: 37.333,
    lineHeight: 1.24,
    layout: 'line',
    textClass: 'text-white',
  },
  mobile: {
    font: 37.333,
    padX: 45,
    // NOT compensated, deliberately. The mobile frames were not part of the 2026-09-04 re-author,
    // and their authored 157 box decomposes as neither padding + cap lines (2*45 + 2*37.333*0.70
    // = 142.3) nor padding + line boxes (172.1) — it is a hand-set frame height, so there is no
    // trim to correct for and compensating would be inventing a number. Mobile stays literal.
    padY: 45,
    radius: 37,
    lineHeight: 1.1,
    layout: 'flat',
    textClass: 'text-reel-chrome-textPrimary',
  },
};

// Below this the copy stops being legible on a small laptop; the tag stays proportional above it.
// One floor is now enough: the old label line sat a step under the body (26.667 vs 37.333) and
// needed its own, which is what PointTag.labelFloor.test.js was pinning. There is no second,
// smaller line on either composition any more, so flooring the body floors everything drawn.
const MIN_FONT_PX = 11;

// A value that already closes with sentence punctuation is prose, not a figure — see tagLine.
const READS_AS_A_SENTENCE = /[.!?]["')\]]?$/;
// Only ever applied to a humanized attribute key ("power reserve", "crystal"), never to curated
// copy, so there is no "of"/"and" case to get wrong and no acronym to flatten.
const titleCase = (text) => text.replace(/\b[a-z]/g, (c) => c.toUpperCase());

/**
 * The desktop line, assembled in the order its frame reads it.
 *
 * The redesign collapses a two-part tag into one phrase, and the frames show the join is literal:
 * movement's "86" + "Hour Power Reserve" is now the single line "86 Hour Power Reserve", and
 * exploded's "Diamond-Cut" + "Hands" is "Diamond-Cut Hands". So `variant` survives the redesign —
 * but only as an ORDER hint. The size hierarchy it used to pick is gone, and with it the
 * length-based heuristic that chose between a side-by-side numeral and a stacked headline: there
 * is one treatment now, so there is nothing left to choose.
 *
 * Both orders are still authored (45:2121 set "Schwarz Etienne" above "ASE 200" but "Sapphire"
 * above "Exhibition Window"), nothing in the data distinguishes them, and it remains the editorial
 * call SPEC-26 OQ-3 parks — so value-first stays the default and `stackedLabelFirst` stays opt-in.
 *
 * Curated prose arrives as `{label: null}` and is already one phrase; it passes straight through.
 *
 * ── Two guards the two-line layout did not need ─────────────────────────────
 * `tag_copy` has no live source (SPEC-26 §0b), so every DYNAMIC tag takes resolveTagContent's
 * fallback path and its label is a raw attribute key run through `humanizeKey` — "crystal",
 * "case finish". While that sat under the value as a 26.667px 70%-opacity caption, a bare
 * lowercase noun read as a label. Welded inline at the same size, weight and colour, it reads as
 * part of the sentence, and two shapes of the result are actively wrong:
 *
 *   1. A value that is already a SENTENCE. crown's `Case_Finish` really is "Stainless steel,
 *      hand-polished by a craftsman." — appending "Case Finish" after the full stop is not a
 *      phrase, it is a noun glued onto prose. A value that closes itself takes no label.
 *   2. A label that is still in KEY CASE. "Domed Sapphire / Double ARDUR crystal" trails off into
 *      lower case mid-line. Title-casing it is the minimum that makes it read as a phrase-part
 *      rather than as a stray word.
 *
 * Neither is a curation fix — the real answer is still OQ-3's curated `tag_copy`, and until that
 * lands a joined key is what a dynamic tag has. These two only stop it reading as a defect.
 *
 * MOBILE IS DELIBERATELY UNAFFECTED: it still renders `value` and `label` as two separate lines,
 * so neither guard applies there and the composition stays bit-identical to Phase 4b.
 */
export function tagLine({ label, value, variant }) {
  const text = value == null ? '' : String(value).trim();
  const caption = label == null ? '' : titleCase(String(label).trim());
  if (!caption || READS_AS_A_SENTENCE.test(text)) return text;
  if (!text) return caption;
  return variant === 'stackedLabelFirst' ? `${caption} ${text}` : `${text} ${caption}`;
}

/**
 * The height ONE desktop/mobile tag actually renders at, in that composition's native frame px.
 *
 * Exported because PointTagLayer has to reserve vertical room for a tag it does not draw, and the
 * only other number available to it — the authored `anchor.h` — is now a poor stand-in: the
 * anchors were sized for the old two-part plate (199-315 native px) while a single-line tag comes
 * out at 158.75. Reserving the larger of those clamps tags well above where they belong.
 */
export function authoredPlateHeight(composition = 'desktop', lines = 1) {
  const box = COMPOSITION[composition] || COMPOSITION.desktop;
  return box.padY * 2 + box.font * box.lineHeight * lines;
}

/**
 * @param {{
 *   label: string|null,
 *   value: string,
 *   lines?: string[],            // mobile only — explicit line breaks, overriding [value, label]
 *   reserve?: string[],          // mobile only — the lines this copy REPLACED; pins the box width (see below)
 *   anchor?: { top: string, left: string },
 *   scale?: number,              // video-box scale (native frame px -> rendered px)
 *   maxWidthPct?: number,        // the design's authored box width, already bounded to the viewport
 *   maxHeightPx?: number,        // the design's authored box height, scaled the same way
 *   variant?: 'stacked'|'stackedLabelFirst',  // desktop copy ORDER only — see tagLine
 *   composition?: 'desktop'|'mobile',
 *   positioned?: boolean,        // false -> a flow child (the mobile row lays tags out itself)
 *   opacity?: number,            // dwell-lifecycle gate (PointTagLayer): 0 while entering/leaving
 *   reveal?: string,             // Reveal.* preset naming HOW it enters/leaves; default fade
 *   reducedMotion?: boolean,     // downgrade the preset to a plain fade
 * }} props
 */
function PointTag({
  label, value, lines, reserve, anchor, scale = 1, maxWidthPct = 32, maxHeightPx, variant,
  composition = 'desktop', positioned = true, opacity = 1, reveal = Reveal.FADE,
  reducedMotion = false,
}) {
  const box = COMPOSITION[composition] || COMPOSITION.desktop;
  const fontSize = Math.max(box.font * scale, MIN_FONT_PX);
  // Padding and radius follow the same ratio to the type as the frame does, so the plate keeps its
  // proportions at every viewport instead of getting relatively chunkier as it shrinks. The ratio
  // is taken against THIS composition's own authored font: the two set their copy at different
  // sizes (42 desktop, 37.333 mobile), so a shared denominator would mis-pad one of them.
  const ratio = fontSize / box.font;
  const padX = box.padX * ratio;
  const padY = box.padY * ratio;
  const radius = box.radius * ratio;

  // Enter/exit is authored in one place (animation/revealTransition.js) rather than inline here,
  // so the desktop corner tween and the mobile fade are the same code path with a different preset.
  // `translateZ(0)` stays the base transform: it isolates this tag's backdrop-blur onto its own
  // compositor layer, so mounting/updating one tag (or the transcript text ticking inside a sibling)
  // does not force the browser to repaint every other blurred layer stacked over the live video —
  // see index.js's video style for the matching layer on the other side of the blur. A corner tween
  // composes onto it; it must not replace it.
  const motion = revealStyle({
    preset: reveal,
    open: opacity > 0,
    durationMs: REVEAL_MS,
    origin: 'top left',
    baseTransform: 'translateZ(0)',
    reducedMotion,
  });

  return (
    <div
      className={`${positioned ? 'absolute' : 'min-w-0'} bg-reel-chrome-panel backdrop-blur-reel-chrome ${box.textClass} font-reel-medium pointer-events-none`}
      style={{
        ...(positioned ? { top: anchor?.top, left: anchor?.left } : null),
        padding: `${padY}px ${padX}px`,
        borderRadius: `${radius}px`,
        fontSize: `${fontSize}px`,
        lineHeight: box.lineHeight,
        // max-content + a maxWidth cap: the plate hugs its resolved copy and only wraps when the
        // copy genuinely exceeds the ceiling — an absolutely-positioned box otherwise shrink-fits
        // against the container edge, which wrapped short values for no content reason.
        ...(positioned ? { width: 'max-content', maxWidth: `${maxWidthPct}%` } : null),
        ...(maxHeightPx ? { maxHeight: `${maxHeightPx}px`, overflow: 'hidden' } : null),
        // Dwell lifecycle (SPEC-26 follow-up 2026-08-18): the layer mounts a tag at 0, flips it to
        // 1 a beat later, and back to 0 before unmounting — `motion` is both halves of that.
        ...motion,
        // A caller passing a fractional `opacity` still gets it: the preset's 0/1 is the gate.
        opacity: motion.opacity * opacity,
        // The preset only knows about the properties IT animates; the blur hint is this component's
        // and has to survive whichever preset is in play.
        willChange: `backdrop-filter, ${motion.willChange}`,
        contain: 'paint',
      }}
    >
      {box.layout === 'flat' ? (
        // The mobile treatment, unchanged in FORM by the 2026-09-04 desktop redesign: stacked
        // lines, all at the body size, the line break the only hierarchy. 45:3297 sets "86 Hour" /
        // "Power Reserve" exactly this way.
        //
        // `lines` exists because mobile now mirrors the desktop copy (staticChapterTags.js) and
        // some of that copy breaks over THREE lines — crown's "Stainless steel, / hand-polished /
        // by a craftsman." The `[value, label]` pair this branch used to render can only ever be
        // two, and it carries a meaning ("a value and its caption") that a static callout does not
        // have. A dynamic tag still arrives as the pair and renders exactly as before.
        <>
          {(lines ?? [value, label]).filter(Boolean).map((text, i) => (
            <span key={`${i}-${text}`} className="block">{text}</span>
          ))}
        </>
      ) : (
        // The desktop treatment: one line, one size, one weight (Figma 369:113 and siblings).
        <span className="block">{tagLine({ label, value, variant })}</span>
      )}
      {/* SPEC-54: MOBILE ONLY. The row shares its width by each plate's max-content, so a copy
          change that is shorter than what it replaced would narrow the box and shift its
          neighbours. `reserve` (the replaced lines) sets the box floor via generated content —
          index.css `.tag-reserve` — so the width stays the authored one while the new copy still
          breaks on its own lines. No text node: nothing can query, select or read it. */}
      {box.layout === 'flat' && reserve?.length > 0 && (
        <span aria-hidden="true" data-testid="tag-reserve" className="tag-reserve" data-reserve={reserve.join('\n')} />
      )}
    </div>
  );
}

export default PointTag;
